import { ThemeConfig, theme } from 'antd';

export type ThemeType = 'dark' | 'light';

/**
 * 获取 PropertyPanel 的 Ant Design 主题配置
 * @param themeType 主题类型：'dark' | 'light'
 * @returns Ant Design ThemeConfig 对象
 */
export const getPropertyPanelTheme = (themeType: ThemeType): ThemeConfig => {
  const isDark = themeType === 'dark';
  
  return {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      // 基础颜色
      colorPrimary: '#1890ff',
      colorSuccess: '#52c41a',
      colorWarning: '#faad14',
      colorError: '#ff4d4f',
      colorInfo: '#1890ff',
      
      // 背景色
      colorBgContainer: isDark ? '#1e1e1e' : '#ffffff',
      colorBgElevated: isDark ? '#262626' : '#ffffff',
      colorBgLayout: isDark ? '#141414' : '#f5f5f5',
      colorBgSpotlight: isDark ? '#262626' : '#ffffff',
      colorBgMask: 'rgba(0, 0, 0, 0.45)',
      
      // 边框色
      colorBorder: isDark ? '#434343' : '#d9d9d9',
      colorBorderSecondary: isDark ? '#303030' : '#f0f0f0',
      
      // 文字颜色 - 关键修复
      colorText: isDark ? '#ffffff' : '#000000d9',
      colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
      colorTextTertiary: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
      colorTextQuaternary: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
      
      // 填充色
      colorFill: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
      colorFillSecondary: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.02)',
      colorFillTertiary: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.01)',
      colorFillQuaternary: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.005)',
      
      // 分割线
      colorSplit: isDark ? '#434343' : '#f0f0f0',
      
      // 阴影
      boxShadow: isDark 
        ? '0 2px 8px 0 rgba(0, 0, 0, 0.15)' 
        : '0 2px 8px 0 rgba(0, 0, 0, 0.05)',
      boxShadowSecondary: isDark 
        ? '0 1px 4px 0 rgba(0, 0, 0, 0.12)' 
        : '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    },
    components: {
      // 折叠面板
      Collapse: {
        headerBg: isDark ? '#262626' : '#fafafa',
        contentBg: isDark ? '#1e1e1e' : '#ffffff',
        colorBorder: isDark ? '#434343' : '#d9d9d9',
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorTextHeading: isDark ? '#ffffff' : '#000000d9',
        borderRadiusLG: 6,
      },
      
      // 表单 - 修复 label 颜色
      Form: {
        itemMarginBottom: 16,
        labelColor: isDark ? '#ffffff' : '#000000d9',
        labelFontSize: 14,
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorTextLabel: isDark ? '#ffffff' : '#000000d9',
        // 确保 label 颜色优先级
        labelRequiredMarkColor: isDark ? '#ff4d4f' : '#ff4d4f',
      },
      
      // 输入框 - 关键修复
      Input: {
        colorBgContainer: isDark ? '#262626' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        colorBorder: isDark ? '#434343' : '#d9d9d9',
        borderRadius: 4,
        paddingInline: 11,
        paddingBlock: 4,
      },
      
      // 数字输入框
      InputNumber: {
        colorBgContainer: isDark ? '#262626' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorBorder: isDark ? '#434343' : '#d9d9d9',
        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        colorBgContainerDisabled: isDark ? '#141414' : '#f5f5f5',
        colorTextDisabled: isDark ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)',
        borderRadius: 4,
        paddingInline: 11,
        paddingBlock: 4,
        controlHeight: 32,
        handleWidth: 22,
        handleFontSize: 12,
      },
      
      // 选择器
      Select: {
        colorBgContainer: isDark ? '#262626' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorTextPlaceholder: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
        colorBorder: isDark ? '#434343' : '#d9d9d9',
        colorBgElevated: isDark ? '#262626' : '#ffffff',
        optionSelectedBg: isDark ? '#1890ff' : '#e6f7ff',
        borderRadius: 4,
      },
      
      // 开关
      Switch: {
        colorPrimary: '#1890ff',
        colorPrimaryHover: '#40a9ff',
        colorTextQuaternary: isDark ? '#434343' : '#d9d9d9',
        colorTextTertiary: isDark ? '#ffffff' : '#000000d9',
        trackHeight: 22,
        trackMinWidth: 44,
        trackPadding: 2,
        innerMinMargin: 2,
        innerMaxMargin: 6,
        handleSize: 18,
        handleShadow: '0 2px 4px 0 rgba(0, 35, 11, 0.2)',
        colorBgContainer: isDark ? '#434343' : '#d9d9d9',
        colorBgContainerDisabled: isDark ? '#262626' : '#f5f5f5',
      },
      
      // 按钮
      Button: {
        colorBgContainer: isDark ? 'transparent' : '#ffffff',
        colorBorder: isDark ? '#434343' : '#d9d9d9',
        colorText: isDark ? '#ffffff' : '#000000d9',
        borderRadius: 4,
      },
      
      // 卡片
      Card: {
        headerBg: isDark ? '#262626' : '#fafafa',
        colorBorderSecondary: isDark ? '#434343' : '#f0f0f0',
        colorBgContainer: isDark ? '#1e1e1e' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorTextHeading: isDark ? '#ffffff' : '#000000d9',
      },
      
      // 颜色选择器
      ColorPicker: {
        colorBgElevated: isDark ? '#262626' : '#ffffff',
        colorText: isDark ? '#ffffff' : '#000000d9',
      },
      
      // 标签
      Typography: {
        colorText: isDark ? '#ffffff' : '#000000d9',
        colorTextSecondary: isDark ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
        colorTextDescription: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
      }
    }
  };
};

// 预定义的主题配置
export const DARK_THEME = getPropertyPanelTheme('dark');
export const LIGHT_THEME = getPropertyPanelTheme('light'); 